import { CommandBar, Spinner, SpinnerSize } from 'office-ui-fabric-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { SORTING_KEYS } from '../../../constants/sortingKeys';
import {
  getAllHearingsInitialize,
  getAllHearingTemplatesInitialize,
  getAllHearingTypesInitialize,
} from '../../../features/actions/api';
import { isFetchingHearingsSelector } from '../../../features/apiFeatures/hearingSlice';
import {
  allHearingTemplatesSelector,
  isFetchingHearingTemplatesSelector,
} from '../../../features/apiFeatures/hearingTemplateSlice';
import {
  allHearingTypesWithNumberOfHearingsSelector,
  hearingTypeByIdSelector,
  isFetchingHearingTypesSelector,
  numberOfHearingsOnHearingTypeByIdSelector,
} from '../../../features/apiFeatures/hearingTypeSlice';
import { getAttr, getEntityId } from '../../../features/helpers/apiHelpers';
import {
  createHearingType,
  deleteHearingType,
  isCreatingHearingTypeSelector,
  isPanelOpenSelector,
  isUpdatingHearingTypeSelector,
  selectedHearingTypeIdSelector,
  setIsPanelOpen,
  setSelectedHearingTypeId,
  updateHearingType,
} from '../../../features/pageFeatures/hearingTypeAdministrationPageSlice';
import { comparators } from '../../../utilities/comparators';
import {
  filterAndSortingKeys,
  noSelectionKey,
} from '../../../utilities/filterAndSortingsUtil';
import { HeaderWithCommandBar } from './HeaderWithCommandBar';
import { CreateHearingTypePanel } from './hearingTypeAdministrationPage/CreateHearingTypePanel';
import { getCommandBarItems } from './hearingTypeAdministrationPage/getCommandBarItems';
import { HearingTypeList } from './hearingTypeAdministrationPage/HearingTypeList';
import { mapHearingType } from './hearingTypeAdministrationPage/mapHearingTypeItem';

const filterHearingTypes = (filter) => (hearingType) =>
  filter === noSelectionKey ||
  (getAttr(hearingType, 'name') &&
    getAttr(hearingType, 'name').toLowerCase().includes(filter.toLowerCase()));

const commandBarStyles = {
  root: {
    backgroundColor: '#FAFAFA',
    paddingRight: '0px',
    Width: 'auto',
  },
};

const hearingTemplateOptionsMapper = (hearingTemplates) => {
  var options = [];
  hearingTemplates.forEach((hearingTemplate) => {
    var item = {
      key: getEntityId(hearingTemplate),
      text: getAttr(hearingTemplate, 'name'),
      iconProps: { iconName: 'KnowledgeArticle' },
      styles: {
        labelWrapper: {
          height: '70px',
          width: '100px',
          maxWidth: '100px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        },
      },
      onRenderLabel: () => (
        <p style={{ padding: '2px', margin: '2px' }}>
          {getAttr(hearingTemplate, 'name')}
        </p>
      ),
    };
    options.push(item);
  });
  return options;
};

const HearingTypeAdministrationPage = () => {
  const dispatch = useDispatch();
  const { t: translator } = useTranslation();

  useEffect(() => {
    dispatch(getAllHearingTypesInitialize());
    dispatch(getAllHearingTemplatesInitialize());
    return function cleanup() {
      dispatch(getAllHearingsInitialize());
    };
  }, [dispatch]);

  const hearingTypes = useSelector(allHearingTypesWithNumberOfHearingsSelector);
  const isFetchingHearingTypes = useSelector(isFetchingHearingTypesSelector);
  const isFetchingHearings = useSelector(isFetchingHearingsSelector);
  const selectedHearingTypeId = useSelector(selectedHearingTypeIdSelector);
  const isPanelOpen = useSelector(isPanelOpenSelector);
  const hearingTemplates = useSelector(allHearingTemplatesSelector);
  const isCreatingHearingType = useSelector(isCreatingHearingTypeSelector);
  const isUpdatingHearingType = useSelector(isUpdatingHearingTypeSelector);
  const selectedHearingType = useSelector(
    hearingTypeByIdSelector(selectedHearingTypeId),
  );
  const isFetchingHearingTemplates = useSelector(
    isFetchingHearingTemplatesSelector,
  );
  const selectedHearingTypeNumberOfHearings = useSelector(
    numberOfHearingsOnHearingTypeByIdSelector(selectedHearingTypeId),
  );

  const [titleFilter, setTitleFilter] = useState(null);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [hearingTypeName, setHearingTypeName] = useState('');
  const [hearingTemplateOptions, setHearingTemplateOptions] = useState([]);
  const [hearingTemplateId, setHearingTemplateId] = useState(null);
  const [isInternal, setIsInternal] = useState(false);
  const [filterAndSortingSelection, setFilterAndSortingSelection] = useState({
    [filterAndSortingKeys.sortingKey]: SORTING_KEYS.ALPHABETICAL,
    [filterAndSortingKeys.title]: noSelectionKey,
  });

  useEffect(() => {
    setHearingTemplateOptions(hearingTemplateOptionsMapper(hearingTemplates));
  }, [hearingTemplates]);

  const onChangeHearingTypeIsActive = (isActive) =>
    selectedHearingTypeId &&
    dispatch(
      updateHearingType({
        hearingTypeId: selectedHearingTypeId,
        isActive: isActive,
      }),
    );

  const openPanel = () => {
    setHearingTypeName('');
    setHearingTemplateId(null);
    setIsInternal(false);
    dispatch(setIsPanelOpen(true));
  };
  const onPanelClose = () => {
    dispatch(setIsPanelOpen(false));
  };

  const onDeleteHearingType = () => {
    selectedHearingTypeId && dispatch(deleteHearingType(selectedHearingTypeId));
  };
  const onConfirmCreateHearingType = () => {
    dispatch(
      createHearingType({
        name: hearingTypeName,
        hearingTemplateId: hearingTemplateId,
        isInternal: isInternal,
      }),
    );
  };

  const filteredHearingTypes = hearingTypes.filter(
    filterHearingTypes(filterAndSortingSelection[filterAndSortingKeys.title]),
  );

  const itemsToRender = filteredHearingTypes.map(mapHearingType(translator));

  const sortedItemsToRender = !!comparators.editHearingTypes[
    filterAndSortingSelection[filterAndSortingKeys.sortingKey]
  ]
    ? itemsToRender.sort(
        comparators.editHearingTypes[
          filterAndSortingSelection[filterAndSortingKeys.sortingKey]
        ],
      )
    : itemsToRender;

  const onSetTitleFilter = (value) => {
    setTitleFilter(value);

    let newState = Object.assign({}, filterAndSortingSelection);
    newState[filterAndSortingKeys.title] = value || noSelectionKey;
    setFilterAndSortingSelection(newState);
  };

  const onSearchBarClick = () => {
    setShowSearchBar(!showSearchBar);
    if (showSearchBar) {
      onSetTitleFilter(null);
    }
  };

  const selectedHearingTypeIsActive = selectedHearingType
    ? getAttr(selectedHearingType, 'isActive')
    : null;

  const isFetchingData =
    isFetchingHearingTypes || isFetchingHearings || isFetchingHearingTemplates;

  const topCommandBarItems = getCommandBarItems(
    translator,
    selectedHearingTypeIsActive,
    onChangeHearingTypeIsActive,
    isUpdatingHearingType,
    selectedHearingTypeNumberOfHearings,
    selectedHearingTypeId,
    onDeleteHearingType,
    openPanel,
    isFetchingData,
  );

  return (
    <div className="admin-page">
      <div className="admin-page-commandBar">
        <CommandBar items={topCommandBarItems} styles={commandBarStyles} />
      </div>
      <div className="admin-page-container">
        <HeaderWithCommandBar
          header={translator('pages.adminHearingTypes.header')}
          titleFilterText={translator('pages.adminHearingTypes.filterText')}
          showTitleFilter={showSearchBar}
          setShowTitleFilter={onSearchBarClick}
          titleFilter={titleFilter}
          setTitleFilter={onSetTitleFilter}
          disableGrid={true}
        />
        {isFetchingData ? (
          <Spinner size={SpinnerSize.large} />
        ) : (
          <HearingTypeList
            hearingTypeItems={sortedItemsToRender}
            filterAndSortingSelection={filterAndSortingSelection}
            setFilterAndSortingSelection={setFilterAndSortingSelection}
            onHearingTypeClick={(hearingTypeId) =>
              dispatch(setSelectedHearingTypeId(hearingTypeId))
            }
            onHearingTypeSelect={(hearingTypeId) =>
              dispatch(setSelectedHearingTypeId(hearingTypeId))
            }
          />
        )}
        <CreateHearingTypePanel
          isOpen={isPanelOpen}
          onClose={onPanelClose}
          hearingTypeName={hearingTypeName}
          setHearingTypeName={setHearingTypeName}
          onConfirmCreateHearingType={onConfirmCreateHearingType}
          isCreatingHearingType={isCreatingHearingType}
          hearingTemplateOptions={hearingTemplateOptions}
          setHearingTemplateId={setHearingTemplateId}
          hearingTemplateId={hearingTemplateId}
          setIsInternal={setIsInternal}
          isInternal={isInternal}
        />
      </div>
    </div>
  );
};
export { HearingTypeAdministrationPage };
